/**
 * UploadPricingModal — shown after a lesson-review upload completes.
 *
 * Lets the instructor pick one of two packages (Review-only vs
 * Review + Video Saved), optionally upgrade to Premium Deep Analysis,
 * and triggers either a Stripe Checkout session OR a founder-free
 * unlock (admin-only). Stays in the same window.
 *
 * Props:
 *   open         — boolean, controls visibility
 *   review       — the in-progress review doc (must include id, duration_seconds, size_bytes)
 *   quote        — pricing quote returned from /api/upload-pricing/quote
 *   founderFree  — whether admin Founder Free Mode is currently ON
 *   onClose      — cancel callback
 *   onPaid       — called with the latest review once processing has been triggered
 *                  (either after Stripe redirect completes or after a free unlock)
 */
import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, CheckCircle2, Video, FileText, Crown, Loader2, X, Lock,
} from "lucide-react";
import { MobileDialog } from "./mobile";
import { MEDIA_FEATURE_ENABLED } from "../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtPrice = (pence) => `£${(pence / 100).toFixed(2)}`;
const fmtDuration = (sec) => {
  const s = Math.max(0, Math.round(sec || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${String(r).padStart(2, "0")}s`;
};
const fmtSize = (bytes) => {
  const mb = (bytes || 0) / (1024 * 1024);
  if (mb < 1) return `${Math.round(mb * 1024)} KB`;
  if (mb < 1000) return `${Math.round(mb)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
};

export default function UploadPricingModal({ open, review, quote, founderFree = false, onClose, onPaid }) {
  const [premium, setPremium] = useState(false);
  const [option, setOption] = useState("review_only"); // review_only | review_plus_video
  const [submitting, setSubmitting] = useState(false);

  const total = useMemo(() => {
    if (!quote) return 0;
    const key = `${option}_${premium ? "premium" : "standard"}`;
    return quote.options?.[key]?.total_pence || 0;
  }, [quote, option, premium]);

  if (!open || !review || !quote) return null;
  // Media quarantine — silent. The pricing flow is V1/V2 lesson-review only.
  if (!MEDIA_FEATURE_ENABLED) return null;

  const confirm = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/upload-pricing/checkout`,
        {
          review_id: review.id,
          option,
          report_type: premium ? "premium" : "standard",
          origin_url: window.location.origin,
        },
        { withCredentials: true },
      );
      if (data.free) {
        toast.success("Founder free mode — processing started");
        onPaid?.({ ...review, status: "queued", paid: true });
        onClose?.();
        return;
      }
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      if (data.already_paid) {
        toast.info("Already paid — processing");
        onPaid?.({ ...review, status: data.status || "queued", paid: true });
        onClose?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MobileDialog
      bareContent
      open={open}
      onClose={onClose}
      testid="upload-pricing-modal"
    >
      <div className="relative w-full sm:max-w-md bg-white dark:bg-slate-900 rounded-t-3xl sm:rounded-3xl shadow-2xl">
        <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wider text-amber-600">Your upload is ready</p>
            <h2 className="text-base font-extrabold text-slate-900 dark:text-white mt-0.5">Choose how to analyse it</h2>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
              Duration: <span className="font-bold">{fmtDuration(review.duration_seconds || quote.duration_seconds)}</span>
              {" · "}Size: <span className="font-bold">{fmtSize(review.size_bytes || quote.size_bytes)}</span>
            </p>
          </div>
          <button
            data-testid="upload-pricing-close"
            onClick={onClose}
            className="p-2 -mr-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {/* Options */}
          <OptionCard
            testid="pricing-opt-review-only"
            active={option === "review_only"}
            onClick={() => setOption("review_only")}
            icon={FileText}
            title="Review + Feedback to Passport"
            bullets={[
              "Full AI lesson evaluation",
              "Written feedback report",
              "Coaching action points",
              "Progress metrics saved to passport",
              "Original media deleted after processing",
            ]}
            price={quote.options[`review_only_${premium ? "premium" : "standard"}`]?.total_pence || 0}
          />
          <OptionCard
            testid="pricing-opt-plus-video"
            active={option === "review_plus_video"}
            onClick={() => setOption("review_plus_video")}
            icon={Video}
            title="Review + Feedback + Video Saved"
            bullets={[
              "Everything in option 1",
              "Original video/audio kept in passport",
              "Replay later · compare future progress",
            ]}
            price={quote.options[`review_plus_video_${premium ? "premium" : "standard"}`]?.total_pence || 0}
            accent="video"
          />

          {/* Premium upsell toggle */}
          <button
            data-testid="pricing-premium-toggle"
            onClick={() => setPremium((p) => !p)}
            className={`w-full rounded-2xl border p-3 flex items-center gap-3 text-left transition-colors ${
              premium
                ? "bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/25 dark:to-orange-900/25 border-amber-300 dark:border-amber-700"
                : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
            }`}
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
              premium ? "bg-amber-500" : "bg-slate-300 dark:bg-slate-700"
            }`}>
              <Crown className={`w-4 h-4 ${premium ? "text-white" : "text-slate-500 dark:text-slate-400"}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-extrabold text-slate-900 dark:text-white">Premium Deep Analysis</p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                Extended coaching insights, scored competencies, deeper transcript study. {premium ? "Included ✓" : `+${fmtPrice(quote.options.review_only_premium.total_pence - quote.options.review_only_standard.total_pence)}`}
              </p>
            </div>
            <div className={`w-10 h-6 rounded-full relative transition-colors ${premium ? "bg-amber-500" : "bg-slate-300 dark:bg-slate-700"}`}>
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${premium ? "left-[18px]" : "left-0.5"}`} />
            </div>
          </button>

          {/* Founder Free banner */}
          {founderFree && (
            <div
              data-testid="founder-free-banner"
              className="rounded-2xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3 flex items-center gap-2 text-xs"
            >
              <Crown className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <p className="text-amber-900 dark:text-amber-100 font-bold">
                Founder Free Mode — this upload will process without payment.
              </p>
            </div>
          )}

          {/* CTA */}
          <button
            data-testid="pricing-confirm-btn"
            onClick={confirm}
            disabled={submitting}
            className="w-full h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 hover:brightness-110 text-white font-extrabold text-sm flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.99] transition-all"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</>
            ) : founderFree ? (
              <><Crown className="w-4 h-4" /> Unlock free (founder) & start processing</>
            ) : (
              <><Lock className="w-4 h-4" /> Continue — pay {fmtPrice(total)} · start processing</>
            )}
          </button>
          <p className="text-[10.5px] text-center text-slate-400">
            Secure Stripe Checkout. Processing starts automatically after payment.
          </p>
        </div>
      </div>
    </MobileDialog>
  );
}

function OptionCard({ testid, active, onClick, icon: Icon, title, bullets, price, accent }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full rounded-2xl border-2 p-4 text-left transition-all active:scale-[0.99] ${
        active
          ? (accent === "video"
              ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20"
              : "border-blue-500 bg-blue-50 dark:bg-blue-900/20")
          : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300"
      }`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          active
            ? (accent === "video" ? "bg-violet-500 text-white" : "bg-blue-600 text-white")
            : "bg-slate-100 dark:bg-slate-700 text-slate-500"
        }`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-extrabold text-slate-900 dark:text-white leading-tight">{title}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className={`text-lg font-extrabold leading-none ${active ? "text-slate-900 dark:text-white" : "text-slate-700 dark:text-slate-200"}`}>
            {`£${(price / 100).toFixed(2)}`}
          </p>
        </div>
      </div>
      <ul className="space-y-1">
        {bullets.map((b, i) => (
          <li key={i} className="text-[11px] text-slate-600 dark:text-slate-300 flex items-start gap-1.5">
            <CheckCircle2 className={`w-3 h-3 mt-0.5 flex-shrink-0 ${active ? "text-emerald-500" : "text-slate-400"}`} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </button>
  );
}
